import { PeriodSelector } from "@/components/school-head/PeriodSelector";
import { useScopedVisits } from "@/hooks/useSchoolHeadData";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { Search } from "lucide-react";

export default function SchoolHeadVisits() {
  const { user } = useAuth();
  const { data: visits = [], isLoading } = useScopedVisits();
  const [query, setQuery] = useState("");

  const { data: students = [] } = useQuery({
    queryKey: ["sh-students-min", user?.schoolId],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      const all: any[] = [];
      let from = 0;
      const size = 1000;
      while (true) {
        const { data, error } = await supabase
          .from("students")
          .select("id,surname,other_names,class")
          .eq("school_id", user!.schoolId)
          .range(from, from + size - 1);
        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < size) break;
        from += size;
      }
      return all;
    },
  });

  const { data: members = [] } = useQuery({
    queryKey: ["sh-members-min", user?.schoolId],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_members")
        .select("id,name,category")
        .eq("school_id", user!.schoolId);
      if (error) throw error;
      return data || [];
    },
  });

  const studentMap = useMemo(() => new Map(students.map((s) => [s.id, s])), [students]);
  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visits
      .map((v) => {
        const s = v.student_id ? studentMap.get(v.student_id) : null;
        const m = v.community_member_id ? memberMap.get(v.community_member_id) : null;
        const person = s
          ? { name: `${s.surname} ${s.other_names || ""}`.trim(), context: s.class || "Student", kind: "Student" }
          : m
          ? { name: m.name, context: m.category, kind: "Staff/Community" }
          : { name: "Unknown", context: "—", kind: "—" };
        return { ...v, person };
      })
      .filter((r) => !q || r.person.name.toLowerCase().includes(q));
  }, [visits, studentMap, memberMap, query]);

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Visits</h1>
          <p className="text-sm text-muted-foreground">
            Read-only view. Detailed diagnoses are abbreviated for privacy.
          </p>
        </div>

        <PeriodSelector />

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name…"
            className="pl-10"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Class / Category</th>
                    <th className="px-4 py-3">Complaint (summary)</th>
                    <th className="px-4 py-3">Follow-up</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>No visits found.</td></tr>
                  ) : (
                    rows.slice(0, 500).map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="px-4 py-2">{r.date ? format(parseISO(r.date), "dd MMM yyyy") : "—"}</td>
                        <td className="px-4 py-2 font-medium">{r.person.name}</td>
                        <td className="px-4 py-2">{r.person.kind}</td>
                        <td className="px-4 py-2">{r.person.context}</td>
                        <td className="px-4 py-2 max-w-[280px] truncate" title={r.complaint || ""}>
                          {(r.complaint || "—").split(/[.;]/)[0].slice(0, 60)}
                        </td>
                        <td className="px-4 py-2">{r.requires_followup ? "Yes" : "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {rows.length > 500 && (
                <div className="border-t px-4 py-2 text-xs text-muted-foreground">
                  Showing first 500 of {rows.length} visits — narrow your search to see more.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
